import { useEffect, useMemo, useState } from "react";
import ChatWindow from "../Chat/ChatWindow";
import UpgradeModal from "./UpgradeModal";
import DemoBanner from "./DemoBanner";
import { buildDemoSession } from "../../demo/demoSession";
import { login } from "../../firebase/auth";
import { trackEvent } from "../../utils/analyticsEvents";

const DEMO_QUESTION_USED_KEY = "insightpulse_demo_question_used";

// ============================================================
// DEMO EXPERIENCE — no sign-up required
// ============================================================
//
// Reuses the app's real ChatWindow (which itself renders the dashboard via
// AutoDashboard in its insights drawer) with an in-memory, non-persisted
// sample session, so the visitor sees the actual product rather than a
// separate mock UI. ChatWindow's `isDemo` prop disables Firestore writes,
// caps the visitor to one successful AI question, and routes restricted
// actions (export, reports) through the upgrade modal below instead of
// performing them.
export default function DemoExperience({ onExit }) {

  // A fresh sample session per mount. Regenerating on every visit (rather
  // than trying to restore a previous in-progress conversation) is the
  // simplest way to guarantee a refresh never leaves the demo in a broken
  // state — there's nothing to fail to restore.
  const [session, setSession] = useState(() => buildDemoSession());

  const [demoQuestionUsed, setDemoQuestionUsed] = useState(() => {
    try {
      return sessionStorage.getItem(DEMO_QUESTION_USED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const [upgradeModal, setUpgradeModal] = useState({ open: false, reason: "default" });

  useEffect(() => {
    trackEvent("demo_started");
    trackEvent("demo_dashboard_viewed");
    // Only on mount — this page's whole purpose is the demo view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openUpgradeModal = (reason = "default") => {
    setUpgradeModal({ open: true, reason });
  };

  const handleDemoQuestionUsed = () => {
    setDemoQuestionUsed(true);
    try {
      sessionStorage.setItem(DEMO_QUESTION_USED_KEY, "1");
    } catch {
      // sessionStorage can be unavailable (private browsing, disabled
      // storage) — the in-memory state above still works for this visit.
    }
  };

  const handleSignUp = useMemo(() => () => {
    trackEvent("demo_signup_clicked");
    login();
  }, []);

  // ChatWindow expects setSessions for its multi-session bookkeeping; the
  // demo only ever has one, non-persisted session, so this is a no-op.
  const noopSetSessions = () => {};

  return (
    <div className="h-screen flex flex-col bg-gray-950 text-white overflow-hidden">

      <DemoBanner onSignUp={handleSignUp} onExit={onExit} />

      <div className="flex-1 min-h-0">
        <ChatWindow
          session={session}
          setActiveSession={setSession}
          setSessions={noopSetSessions}
          onOpenSubscription={() => openUpgradeModal("question-limit")}
          isDemo
          demoQuestionUsed={demoQuestionUsed}
          onDemoQuestionUsed={handleDemoQuestionUsed}
          onRestrictedAction={openUpgradeModal}
        />
      </div>

      <UpgradeModal
        open={upgradeModal.open}
        reason={upgradeModal.reason}
        onClose={() => setUpgradeModal((prev) => ({ ...prev, open: false }))}
        onSignUp={handleSignUp}
      />

    </div>
  );
}
